import { ActionTree } from "vuex";
import * as Sentry from "@sentry/vue";
import { StateInterface } from "src/store";
import { EventsStateInterface } from "./state";
import API from "src/services/api/api";
import Utils from "src/services/helpers/utils";
import LocalDb from "src/services/db";
import {
  EventItem,
  EventItemStateRate,
  EventItemStateSetState,
} from "../../models";
import ma_objects from "src/modules/ma/classes/ma_objects";
import MaRefs from "src/modules/ma/classes/ma_refs";

const actions: ActionTree<EventsStateInterface, StateInterface> = {
  /**
   * Инициализация сессии
   */
  async initSession(context, payload: { session_guid: string }): Promise<void> {
    if (!context.state.sessions[payload.session_guid]) {
      // инициализируем сессию в state и подгружаем события из IndexedDB
      context.commit("initSession", { session_guid: payload.session_guid });
      await context.dispatch("initEventsFromDb", {
        session_guid: payload.session_guid,
      });

      // подгружаем события с сервера
      let length = context.state.sessions[payload.session_guid].events.length;
      const loadEventsPromises = [
        context.dispatch("loadNextEvents", {
          session_guid: payload.session_guid,
        }),
        length < context.state.page_size
          ? context.dispatch("loadPreviousEvents", {
              session_guid: payload.session_guid,
              page_size: context.state.page_size - length,
            })
          : Promise.resolve(),
      ];
      Promise.all(loadEventsPromises).then(async () => {
        //#region инициализация приветственного сообщения
        length = context.state.sessions[payload.session_guid].events.length;
        if (!length) {
          const initReaction =
            context.rootGetters["settings/fullSettings"].initReaction;
          if (initReaction) {
            const initReactionRef = ma_objects.ref2str(initReaction.valueRef);
            const events = (
              await API.cf.make_reaction(payload.session_guid, initReactionRef)
            )?.events;
            context.dispatch(
              "eventsUtils/playEventsNow",
              { events: events },
              { root: true }
            );
            context.dispatch("pushEvents", { events: events });
          }
        }
        //#endregion
      });
    }

    return Promise.resolve();
  },

  /**
   * Получение событий из IndexedDB и добавление их в state
   */
  async initEventsFromDb(
    context,
    payload: { session_guid: string }
  ): Promise<void> {
    const db = await LocalDb;
    const events = await db.events.getAllBySession(payload.session_guid);
    if (events.length) {
      context.commit("unshiftEvents", {
        events: events.slice(-context.state.page_size), // берем из IndexedDB только последнюю страницу
      });
      // TODO: удалять старые events из IndexedDB (надо ли?)
    }

    return Promise.resolve();
  },

  /**
   * Получение истории событий с сервера
   */
  async loadPreviousEvents(
    context,
    payload: { session_guid: string; page_size?: number }
  ): Promise<EventItem[]> {
    await context.dispatch("initSession", {
      session_guid: payload.session_guid,
    });
    const length = context.state.sessions[payload.session_guid].events.length;
    const date = length
      ? context.state.sessions[payload.session_guid].events[0].at
      : new Date().toISOString();
    const events = await API.cf.get_events({
      session_guid: payload.session_guid,
      date: date,
      is_reversed: true,
      take: payload.page_size || context.state.page_size,
      types_end_to_end: context.state.pagination_event_types,
    });
    if (events.length) {
      context.commit("unshiftEvents", { events: events });
    }

    return events;
  },

  /**
   * Получение новых событий с сервера
   */
  async loadNextEvents(
    context,
    payload: { session_guid: string }
  ): Promise<EventItem[]> {
    await context.dispatch("initSession", {
      session_guid: payload.session_guid,
    });
    const length = context.state.sessions[payload.session_guid].events.length;
    const date = length
      ? context.state.sessions[payload.session_guid].events[length - 1].at
      : new Date().toISOString();
    const events = await API.cf.get_events({
      session_guid: payload.session_guid,
      date: date,
      is_reversed: false,
    });
    await context.dispatch("pushEvents", { events: events });

    return Promise.resolve(events);
  },

  /**
   * Сохранение событий в state и IndexedDB
   */
  async pushEvents(context, payload: { events: EventItem[] }): Promise<void> {
    if (payload.events?.length) {
      await Promise.all(
        payload.events.map((x) =>
          context.dispatch("initSession", { session_guid: x.session_guid })
        )
      );
      context.commit("pushEvents", { events: payload.events });
      //#region дублируем сообщение в старое хранилище
      payload.events.forEach((event) => {
        context.dispatch("chat/addMessage", EventItem.toOldMessage(event), {
          root: true,
        });
      });
      //#endregion
      for (const event of payload.events) {
        context.commit("markEventAsNew", { event });
      }
      //#region обработка новых событий и сохранение событий в IndexedDB
      LocalDb.then(async (db) => {
        for (let i = 0; i < payload.events.length; i++) {
          const event = payload.events[i];
          try {
            const exist = !!(await db.events.getByKey(event.guid));
            db.events.put(event.guid, event);
            if (!exist) {
              const event_types = MaRefs.str.ds_event_types;
              if (MaRefs.compareAsStr(event.type_ref, event_types.rate)) {
                //#region обрабатываем событие rate
                const event_rate =
                  event as unknown as EventItem<EventItemStateRate>;
                if (event_rate.state?.target_type == "event") {
                  context.dispatch("syncEvent", {
                    event_guid: event_rate.state.target_guid,
                  });
                }
                //#endregion
              } else if (
                MaRefs.compareAsStr(event.type_ref, event_types.set_state)
              ) {
                //#region обрабатываем событие set_state
                const event_set_state =
                  event as unknown as EventItem<EventItemStateSetState>;
                context.dispatch(
                  "sessionsUtils/setSessionState",
                  {
                    session_guid: event_set_state.session_guid,
                    last_state: event_set_state.state?.last_state,
                  },
                  { root: true }
                );
                //#endregion
              }
            }
          } catch (error) {
            console.log("ERROR[events/pushEvents]", error);
            Sentry.captureException(error);
          }
        }
      });
      //#endregion
    }

    return Promise.resolve();
  },

  /**
   * Актуализация события: получаем событие с сервера и сохраняем его в IndexedDB
   */
  async syncEvent(context, payload: { event_guid: string }) {
    const event = await API.cf.get_event(payload.event_guid);
    await context.dispatch("pushEvents", { events: [event] });
  },

  /**
   * Оценка события
   */
  async rateEvent(context, payload: { event_guid: string; rate: 1 | -1 }) {
    await API.cf.set_rate({
      event_guid: payload.event_guid,
      rate: payload.rate,
      source: context.rootGetters["settings/allSettings"].clientSource,
    });
    try {
      await context.dispatch("syncEvent", { event_guid: payload.event_guid });
    } catch (error) {
      //#region при возникновении ошибки - сохраняем инфо о rate локально
      const session_guids = Object.keys(context.state.sessions);
      for (let i = 0; i < session_guids.length; i++) {
        const session_guid = session_guids[i];
        const index = context.state.sessions[session_guid].events.findIndex(
          (x) => x.guid == payload.event_guid
        );
        if (index > -1) {
          const event = Utils.clone<EventItem>(
            context.state.sessions[session_guid].events[index]
          );
          const rate = {
            rate: payload.rate,
            user_ref: context.rootState.user.id,
          };
          if (!event.rates || !event.rates.list) {
            event.rates = { list: [rate] };
          } else {
            event.rates.list.push(rate);
          }
          await context.dispatch("pushEvents", { events: [event] });
        }
      }
      //#endregion
      Sentry.captureException(error);
    }

    return Promise.resolve();
  },
};

export default actions;
